import { useMemo } from 'react';

import type { AiUsageMonth } from '@/api/types';
import { DataTable, type DataTableColumn } from '@/design-system';

interface MonthlyTableProps {
  months: readonly AiUsageMonth[];
}

const monthLabel = (epochMs: number): string =>
  new Date(epochMs).toLocaleString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
const usd = (value: number): string =>
  value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const integer = (value: number): string => value.toLocaleString();
const tokensTotal = (by: Record<string, number | string>): number =>
  Object.values(by).reduce<number>((total, value) => total + Number(value), 0);

export function MonthlyTable({ months }: MonthlyTableProps) {
  const columns = useMemo<readonly DataTableColumn<AiUsageMonth>[]>(() => [
    { id: 'month', header: 'Month', getValue: (item) => item.monthStartMs, renderCell: (item) => monthLabel(item.monthStartMs) },
    { id: 'actions', header: 'Actions', getValue: (item) => item.userActions, renderCell: (item) => integer(item.userActions) },
    { id: 'aoai', header: 'AOAI calls', getValue: (item) => item.aoaiCalls, renderCell: (item) => integer(item.aoaiCalls) },
    { id: 'di', header: 'DI calls', getValue: (item) => item.diCalls, renderCell: (item) => integer(item.diCalls) },
    { id: 'pages', header: 'Pages', getValue: (item) => item.pages, renderCell: (item) => integer(item.pages) },
    { id: 'tokIn', header: 'Tokens in', getValue: (item) => tokensTotal(item.tokensInByModel), renderCell: (item) => integer(tokensTotal(item.tokensInByModel)) },
    { id: 'tokOut', header: 'Tokens out', getValue: (item) => tokensTotal(item.tokensOutByModel), renderCell: (item) => integer(tokensTotal(item.tokensOutByModel)) },
    { id: 'tokenCost', header: 'Token $', getValue: (item) => item.tokenCostUsd, renderCell: (item) => usd(item.tokenCostUsd) },
    { id: 'diCost', header: 'DI $', getValue: (item) => item.diCostUsd, renderCell: (item) => usd(item.diCostUsd) },
    { id: 'total', header: 'Total $', getValue: (item) => item.totalCostUsd, renderCell: (item) => usd(item.totalCostUsd) },
    { id: 'perAction', header: '$/action', getValue: (item) => item.costPerAction, renderCell: (item) => item.costPerAction === null ? '—' : usd(item.costPerAction) },
  ], []);

  return <DataTable ariaLabel="Monthly AI usage and cost breakdown" columns={columns} getRowId={(item) => String(item.monthStartMs)} items={months} tableId="ai-usage-monthly" />;
}
